import { useState } from "react";
import {
  Users, Building, CalendarDays, DollarSign, TrendingUp, TrendingDown,
  AlertTriangle, Server, Globe, Lock, FileText, Download, RefreshCw,
  Eye, Edit, Trash2, Phone, CheckCircle, XCircle, Clock, BarChart3,
  Crown, Search, UserCheck, Smartphone, Wifi, Shield, Bell, Star,
  Activity, ChevronDown, Mail, Database, Zap, MapPin
} from "lucide-react";

const STATS = {
  utentiTotali: 0, utentiNuoviMese: 0, utentiPremium: 0, utentiStarter: 0,
  struttureTotali: 0, struttureNuoveMese: 0,
  prenotazioniMese: 0, prenotazioniTotali: 0,
  fatturatoMese: 0, fatturatoAnno: 0,
  guestAppSessions: 0, checkinOnlineMese: 0,
  serverUptime: "—", responseTime: "—", storagePerc: 0, cpuPerc: 0, ramPerc: 0,
  ticketAperti: 0, ticketCritici: 0,
};

const CLIENTI: any[] = [];
const TICKETS: any[] = [];
const STRUTTURE_RECENTI: any[] = [];
const MESE_LABELS = ["Nov", "Dic", "Gen", "Feb", "Mar", "Apr"];
const FATTURATO_DATA = [0, 0, 0, 0, 0, 0];
const UTENTI_DATA = [0, 0, 0, 0, 0, 0];

// ── COMPONENTI INTERNI ──────────────────────────────────────────────────────────
function StatCard({ label, value, sub, icon: Icon, color, bg }: any) {
  return (
    <div className={`rounded-2xl border p-4 ${bg}`}>
      <div className="flex items-start justify-between mb-2">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${color}`} style={{ background: "rgba(255,255,255,0.5)" }}>
          <Icon className="h-4 w-4" />
        </div>
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full bg-white/60 ${color}`}>{sub}</span>
      </div>
      <div className="text-2xl font-bold text-foreground">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function PriorBadge({ p }: { p: string }) {
  const m: Record<string, string> = {
    "Critica": "bg-red-100 text-red-800", "Alta": "bg-orange-100 text-orange-800",
    "Media": "bg-yellow-100 text-yellow-800", "Bassa": "bg-blue-100 text-blue-800",
  };
  return <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${m[p] ?? "bg-gray-100 text-gray-700"}`}>{p}</span>;
}

function StatoBadge({ s }: { s: string }) {
  const m: Record<string, string> = {
    "Aperto": "bg-red-50 text-red-700", "In lavorazione": "bg-amber-50 text-amber-700",
    "Risolto": "bg-emerald-50 text-emerald-700", "Chiuso": "bg-gray-100 text-gray-600",
    "Attivo": "bg-emerald-50 text-emerald-700", "Sospeso": "bg-red-50 text-red-700",
    "Attiva": "bg-emerald-50 text-emerald-700", "In manutenzione": "bg-amber-50 text-amber-700",
  };
  return <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${m[s] ?? "bg-gray-100 text-gray-600"}`}>{s}</span>;
}

function PianoBadge({ p }: { p: string }) {
  const m: Record<string, string> = {
    "Premium": "bg-primary/10 text-primary",
    "Business": "bg-amber-100 text-amber-700",
    "Starter": "bg-gray-100 text-gray-600",
  };
  return <span className={`text-xs px-2.5 py-1 rounded-full font-semibold ${m[p] ?? "bg-gray-100 text-gray-600"}`}>{p}</span>;
}

function ProgressBar({ perc, color }: { perc: number; color: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${perc}%` }} />
      </div>
      <span className="text-xs font-medium text-foreground w-8 text-right">{perc}%</span>
    </div>
  );
}

// ── COMPONENTE PRINCIPALE ───────────────────────────────────────────────────────
const TABS = [
  { id: "panoramica", label: "Panoramica",  icon: BarChart3 },
  { id: "clienti",    label: "Clienti",     icon: Users },
  { id: "strutture",  label: "Strutture",   icon: Building },
  { id: "ticket",     label: "Supporto",    icon: FileText },
  { id: "sistema",    label: "Sistema",     icon: Server },
];

export default function SuperAdminDashboard() {
  const [tab, setTab] = useState("panoramica");
  const [cerca, setCerca] = useState("");
  const [filtroStato, setFiltroStato] = useState("tutti");

  const clientiFiltrati = CLIENTI.filter(c => {
    const q = cerca.toLowerCase();
    const matchQ = c.nome.toLowerCase().includes(q) || c.email.toLowerCase().includes(q);
    const matchS = filtroStato === "tutti" || c.stato === filtroStato;
    return matchQ && matchS;
  });

  return (
    <div className="-m-4 md:-m-6">
      {/* ── HEADER ── */}
      <div className="bg-gradient-to-r from-[#1a1814] to-[#2d2820] px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/20 border border-primary/30 flex items-center justify-center">
            <Crown className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h1 className="text-white font-bold text-lg">Super Admin — Hedita</h1>
            <p className="text-white/60 text-xs">Pannello di controllo piattaforma · {new Date().toLocaleDateString("it-IT")}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 px-3 py-1.5 rounded-full text-xs font-medium">
            <div className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Sistema online
          </div>
          <button className="flex items-center gap-1.5 bg-white/10 text-white px-3 py-1.5 rounded-xl text-xs hover:bg-white/20 transition-colors">
            <RefreshCw className="h-3.5 w-3.5" /> Aggiorna
          </button>
        </div>
      </div>

      <div className="p-4 md:p-6">
        {/* ── KPI CARDS ── */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <StatCard label="Utenti totali"      value={STATS.utentiTotali.toLocaleString()} sub={`+${STATS.utentiNuoviMese} questo mese`} icon={Users}       color="text-blue-600"   bg="bg-blue-50 border-blue-200" />
          <StatCard label="Fatturato mese"     value={`€${STATS.fatturatoMese.toLocaleString()}`} sub="+9% vs mese scorso" icon={DollarSign}  color="text-emerald-600" bg="bg-emerald-50 border-emerald-200" />
          <StatCard label="Strutture attive"   value={STATS.struttureTotali.toLocaleString()} sub={`+${STATS.struttureNuoveMese} nuove`}   icon={Building}    color="text-amber-600"  bg="bg-amber-50 border-amber-200" />
          <StatCard label="Ticket aperti"      value={STATS.ticketAperti}          sub={`${STATS.ticketCritici} critici`}     icon={AlertTriangle} color="text-red-600"    bg="bg-red-50 border-red-200" />
        </div>

        {/* ── TAB BAR ── */}
        <div className="flex gap-1 mb-5 overflow-x-auto pb-1">
          {TABS.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-colors flex-shrink-0"
              style={{
                backgroundColor: tab === t.id ? "hsl(var(--primary))" : "hsl(var(--card))",
                color: tab === t.id ? "hsl(var(--primary-foreground))" : "hsl(var(--muted-foreground))",
                border: `1px solid ${tab === t.id ? "transparent" : "hsl(var(--border))"}`,
              }}>
              <t.icon className="h-4 w-4" /> {t.label}
            </button>
          ))}
        </div>

        {/* ─────────── PANORAMICA ─────────── */}
        {tab === "panoramica" && (
          <div className="space-y-5">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

              {/* Grafico fatturato (barre simulate) */}
              <div className="lg:col-span-2 bg-card border border-border rounded-2xl p-5">
                <div className="flex items-center justify-between mb-5">
                  <h3 className="font-semibold text-foreground">Fatturato mensile</h3>
                  <span className="text-xs text-muted-foreground">Ultimi 6 mesi</span>
                </div>
                <div className="flex items-end gap-3 h-40">
                  {FATTURATO_DATA.map((v, i) => {
                    const max = Math.max(...FATTURATO_DATA);
                    const h = Math.round((v / max) * 100);
                    return (
                      <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                        <span className="text-[10px] text-muted-foreground">€{(v/1000).toFixed(0)}k</span>
                        <div className="w-full rounded-t-lg" style={{ height: `${h}%`, backgroundColor: i === FATTURATO_DATA.length - 1 ? "hsl(var(--primary))" : "hsl(var(--primary)/0.3)" }} />
                        <span className="text-[10px] text-muted-foreground">{MESE_LABELS[i]}</span>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Distribuzione piani */}
              <div className="bg-card border border-border rounded-2xl p-5">
                <h3 className="font-semibold text-foreground mb-5">Distribuzione piani</h3>
                <div className="space-y-4">
                  {[
                    { label: "Starter",  n: STATS.utentiStarter,  perc: Math.round(STATS.utentiStarter/STATS.utentiTotali*100),  color: "bg-gray-400" },
                    { label: "Premium",  n: STATS.utentiPremium,  perc: Math.round(STATS.utentiPremium/STATS.utentiTotali*100),  color: "bg-primary" },
                    { label: "Business", n: 156, perc: 12, color: "bg-amber-500" },
                  ].map(p => (
                    <div key={p.label}>
                      <div className="flex justify-between text-sm mb-1">
                        <span className="text-foreground font-medium">{p.label}</span>
                        <span className="text-muted-foreground">{p.n} ({p.perc}%)</span>
                      </div>
                      <ProgressBar perc={p.perc} color={p.color} />
                    </div>
                  ))}
                </div>
                <div className="border-t border-border mt-5 pt-4 space-y-2">
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Guest App sessioni/mese</span><span className="font-semibold text-foreground">{STATS.guestAppSessions.toLocaleString()}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Check-in online mese</span><span className="font-semibold text-foreground">{STATS.checkinOnlineMese.toLocaleString()}</span></div>
                  <div className="flex justify-between text-sm"><span className="text-muted-foreground">Prenotazioni tot.</span><span className="font-semibold text-foreground">{STATS.prenotazioniTotali.toLocaleString()}</span></div>
                </div>
              </div>
            </div>

            {/* Crescita utenti */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground">Crescita utenti</h3>
                <span className="text-xs text-emerald-600 font-medium bg-emerald-50 px-2.5 py-1 rounded-full">+{STATS.utentiNuoviMese} questo mese</span>
              </div>
              <div className="flex items-end gap-4 h-20">
                {UTENTI_DATA.map((v, i) => {
                  const min = Math.min(...UTENTI_DATA);
                  const max = Math.max(...UTENTI_DATA);
                  const h = Math.round(((v - min) / (max - min)) * 60) + 20;
                  return (
                    <div key={i} className="flex-1 flex flex-col items-center gap-1.5">
                      <div className="w-full rounded-t-lg bg-blue-200" style={{ height: `${h}px` }} />
                      <span className="text-[10px] text-muted-foreground">{MESE_LABELS[i]}</span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Ticket recenti */}
            <div className="bg-card border border-border rounded-2xl p-5">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-foreground">Ticket recenti</h3>
                <button onClick={() => setTab("ticket")} className="text-xs text-primary hover:underline">Vedi tutti →</button>
              </div>
              <div className="space-y-2">
                {TICKETS.slice(0, 3).map(t => (
                  <div key={t.id} className="flex items-center gap-3 py-2.5 border-b border-border last:border-0">
                    <PriorBadge p={t.priorita} />
                    <span className="flex-1 text-sm text-foreground truncate">{t.oggetto}</span>
                    <span className="text-xs text-muted-foreground">{t.cliente}</span>
                    <StatoBadge s={t.stato} />
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* ─────────── CLIENTI ─────────── */}
        {tab === "clienti" && (
          <div className="bg-card border border-border rounded-2xl">
            <div className="p-5 border-b border-border flex flex-col md:flex-row md:items-center gap-3">
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input value={cerca} onChange={e => setCerca(e.target.value)}
                  className="w-full border border-border rounded-xl pl-10 pr-4 py-2.5 text-sm bg-background focus:outline-none focus:ring-2 focus:ring-primary/30"
                  placeholder="Cerca per nome o email…" />
              </div>
              <div className="flex gap-2">
                <div className="relative">
                  <select value={filtroStato} onChange={e => setFiltroStato(e.target.value)}
                    className="appearance-none border border-border rounded-xl pl-3.5 pr-8 py-2.5 text-sm bg-background focus:outline-none">
                    <option value="tutti">Tutti gli stati</option>
                    <option value="Attivo">Attivi</option>
                    <option value="Sospeso">Sospesi</option>
                  </select>
                  <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground pointer-events-none" />
                </div>
                <button className="flex items-center gap-1.5 border border-border rounded-xl px-3.5 py-2.5 text-sm text-muted-foreground hover:bg-muted/40 transition-colors">
                  <Download className="h-4 w-4" /> Esporta
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-muted/30">
                    {["Cliente", "Piano", "Stato", "Strutture", "Fatturato tot.", "Ultimo accesso", "Azioni"].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {clientiFiltrati.map(c => (
                    <tr key={c.id} className="border-t border-border hover:bg-muted/30 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-xl bg-primary/10 text-primary font-bold text-sm flex items-center justify-center flex-shrink-0">
                            {c.nome.split(" ").map(n => n[0]).join("").slice(0, 2)}
                          </div>
                          <div>
                            <div className="font-medium text-foreground text-sm flex items-center gap-1.5">{c.paese} {c.nome}</div>
                            <div className="text-xs text-muted-foreground">{c.email}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-5 py-4"><PianoBadge p={c.piano} /></td>
                      <td className="px-5 py-4"><StatoBadge s={c.stato} /></td>
                      <td className="px-5 py-4 text-sm font-medium text-foreground">{c.strutture}</td>
                      <td className="px-5 py-4 text-sm font-semibold text-emerald-600">€{c.fatturato.toLocaleString()}</td>
                      <td className="px-5 py-4 text-xs text-muted-foreground">{c.lastLogin}</td>
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-1">
                          <button className="p-1.5 rounded-lg hover:bg-primary/10 text-muted-foreground hover:text-primary transition-colors"><Eye className="h-4 w-4" /></button>
                          <button className="p-1.5 rounded-lg hover:bg-amber-50 text-muted-foreground hover:text-amber-600 transition-colors"><Edit className="h-4 w-4" /></button>
                          <button className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors"><Lock className="h-4 w-4" /></button>
                          <button className="p-1.5 rounded-lg hover:bg-blue-50 text-muted-foreground hover:text-blue-500 transition-colors"><Mail className="h-4 w-4" /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="p-4 border-t border-border flex items-center justify-between text-xs text-muted-foreground">
              <span>{clientiFiltrati.length} risultati su {CLIENTI.length} clienti totali</span>
              <span>Pagina 1 di 208</span>
            </div>
          </div>
        )}

        {/* ─────────── STRUTTURE ─────────── */}
        {tab === "strutture" && (
          <div className="space-y-4">
            <div className="grid grid-cols-3 gap-3 mb-2">
              <div className="bg-card border border-border rounded-2xl p-4 text-center">
                <div className="text-2xl font-bold text-foreground">{STATS.struttureTotali.toLocaleString()}</div>
                <div className="text-xs text-muted-foreground mt-0.5">Strutture totali</div>
              </div>
              <div className="bg-card border border-border rounded-2xl p-4 text-center">
                <div className="text-2xl font-bold text-emerald-600">3187</div>
                <div className="text-xs text-muted-foreground mt-0.5">Attive</div>
              </div>
              <div className="bg-card border border-border rounded-2xl p-4 text-center">
                <div className="text-2xl font-bold text-amber-600">234</div>
                <div className="text-xs text-muted-foreground mt-0.5">In manutenzione</div>
              </div>
            </div>
            <div className="bg-card border border-border rounded-2xl overflow-hidden">
              <div className="p-5 border-b border-border font-semibold text-foreground">Strutture recenti</div>
              <table className="w-full">
                <thead>
                  <tr className="bg-muted/30">
                    {["Struttura", "Tipo", "Proprietario", "Città", "Camere", "Stato", "Creata"].map(h => (
                      <th key={h} className="px-5 py-3 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {STRUTTURE_RECENTI.map(s => (
                    <tr key={s.id} className="border-t border-border hover:bg-muted/30 transition-colors">
                      <td className="px-5 py-4 font-medium text-foreground text-sm">{s.nome}</td>
                      <td className="px-5 py-4 text-xs text-muted-foreground">{s.tipo}</td>
                      <td className="px-5 py-4 text-sm text-foreground">{s.proprietario}</td>
                      <td className="px-5 py-4 text-sm text-muted-foreground"><MapPin className="h-3.5 w-3.5 inline mr-1" />{s.citta}</td>
                      <td className="px-5 py-4 text-sm font-medium text-foreground">{s.camere}</td>
                      <td className="px-5 py-4"><StatoBadge s={s.stato} /></td>
                      <td className="px-5 py-4 text-xs text-muted-foreground">{s.creata}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ─────────── SUPPORTO ─────────── */}
        {tab === "ticket" && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-3 mb-2">
              {[
                { label: "Aperti",       n: 12, bg: "bg-red-50 border-red-200 text-red-700" },
                { label: "In lavorazione",n: 8,  bg: "bg-amber-50 border-amber-200 text-amber-700" },
                { label: "Risolti (mese)",n: 47, bg: "bg-emerald-50 border-emerald-200 text-emerald-700" },
                { label: "Critici",      n: STATS.ticketCritici, bg: "bg-red-100 border-red-300 text-red-800" },
              ].map(s => (
                <div key={s.label} className={`rounded-2xl border p-4 text-center ${s.bg}`}>
                  <div className="text-2xl font-bold">{s.n}</div>
                  <div className="text-xs font-medium mt-0.5">{s.label}</div>
                </div>
              ))}
            </div>
            {TICKETS.map(t => (
              <div key={t.id} className="bg-card border border-border rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <PriorBadge p={t.priorita} />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-foreground text-sm mb-1">{t.oggetto}</div>
                    <div className="text-xs text-muted-foreground">
                      {t.cliente} · {t.categoria} · {t.data} · Assegnato: {t.assegnatoA}
                    </div>
                  </div>
                  <StatoBadge s={t.stato} />
                </div>
                <div className="flex gap-2 mt-3 pt-3 border-t border-border">
                  <button className="flex-1 text-xs border border-border py-1.5 rounded-lg text-muted-foreground hover:bg-muted/40 transition-colors">Gestisci</button>
                  <button className="flex-1 text-xs bg-primary text-primary-foreground py-1.5 rounded-lg font-medium hover:bg-primary/90 transition-colors">Rispondi</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ─────────── SISTEMA ─────────── */}
        {tab === "sistema" && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2"><Server className="h-4 w-4 text-primary" /> Performance Server</h3>
              <div className="space-y-4">
                {[
                  { label: "CPU Load",    perc: STATS.cpuPerc,     color: "bg-emerald-400" },
                  { label: "RAM",         perc: STATS.ramPerc,     color: "bg-amber-400" },
                  { label: "Storage",     perc: STATS.storagePerc, color: "bg-blue-400" },
                  { label: "Bandwidth",   perc: 43,                color: "bg-primary" },
                ].map(m => (
                  <div key={m.label}>
                    <div className="flex justify-between text-sm mb-1.5">
                      <span className="text-foreground">{m.label}</span>
                    </div>
                    <ProgressBar perc={m.perc} color={m.color} />
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-5">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2"><Activity className="h-4 w-4 text-primary" /> Stato Servizi</h3>
              <div className="space-y-3">
                {[
                  { label: "API Server",       stato: "Online",      uptime: "99.8%" },
                  { label: "Database",         stato: "Online",      uptime: "99.9%" },
                  { label: "iCal Sync Engine", stato: "Online",      uptime: "99.5%" },
                  { label: "Guest App CDN",    stato: "Online",      uptime: "100%" },
                  { label: "Email Service",    stato: "Online",      uptime: "99.7%" },
                  { label: "Backup System",    stato: "Completato",  uptime: "—" },
                ].map(s => (
                  <div key={s.label} className="flex items-center justify-between py-2 border-b border-border last:border-0">
                    <div className="flex items-center gap-2.5">
                      <div className={`w-2 h-2 rounded-full ${s.stato === "Online" || s.stato === "Completato" ? "bg-emerald-500" : "bg-red-500"}`} />
                      <span className="text-sm text-foreground">{s.label}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-muted-foreground">Uptime: {s.uptime}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${s.stato === "Online" || s.stato === "Completato" ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`}>{s.stato}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-card border border-border rounded-2xl p-5 md:col-span-2">
              <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2"><Database className="h-4 w-4 text-primary" /> Statistiche Database</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Query/sec",       value: "1,247" },
                  { label: "Storage usato",   value: "2.3 TB / 5 TB" },
                  { label: "Ultimo backup",   value: "Oggi 03:00" },
                  { label: "Response time",   value: STATS.responseTime },
                ].map(s => (
                  <div key={s.label} className="bg-muted/30 rounded-xl p-3">
                    <div className="text-xs text-muted-foreground mb-1">{s.label}</div>
                    <div className="font-semibold text-foreground">{s.value}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
